import { Box, Button, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { SignupResultLayout } from '../SignupResultLayout/SignupResultLayout';
import { signupAccountExistsStyles } from './SignupAccountExists.styles';

interface SignupAccountExistsProps {
  isArchived: boolean;
  signupReturnPath?: string;
  inline?: {
    programName: string;
    schoolName: string;
    password: string;
    isWorking: boolean;
    error: string;
    onPasswordChange: (password: string) => void;
    onUseDifferentAccount: () => void;
  };
}

export function SignupAccountExists({isArchived, inline, signupReturnPath = '/signup'}: SignupAccountExistsProps) {
  const theme = useTheme();

  if (!inline) {
    return (
      <SignupResultLayout title={isArchived ? 'Your Exploros account is archived' : 'You already have an account'}>
        <Typography sx={signupAccountExistsStyles.resultMessage(theme)}>
          {isArchived ? 'Log in with your existing password to restore your account.' : 'Log in with your existing account, or use password reset if you need help signing in.'}
        </Typography>
        <Box sx={signupAccountExistsStyles.resultActions()}>
          <Button href="/login" color="success" variant="contained">Log in</Button>
          <Button href={signupReturnPath} color="success" variant="outlined">Return to signup</Button>
        </Box>
      </SignupResultLayout>
    );
  }

  // The Account step owns the form so Enter uses the same sign-in handler as its submit button.
  return (
    <Box component="section" aria-labelledby="account-exists-heading" sx={signupAccountExistsStyles.inlineResult()}>
      <Typography id="account-exists-heading" component="h2" sx={signupAccountExistsStyles.accountHeading()}>You already have an Exploros account</Typography>
      <Box role="status" sx={signupAccountExistsStyles.accountInfo(theme)}>
        <Box component="span" aria-hidden="true" sx={signupAccountExistsStyles.infoIcon()}>i</Box>
        <Typography>{isArchived ? `This Exploros account is archived. Enter your password to restore it and continue with ${inline.programName}${inline.schoolName ? ` at ${inline.schoolName}` : ''}.` : `An account already exists for this sign-in. Sign in to continue with ${inline.programName}${inline.schoolName ? ` at ${inline.schoolName}` : ''}.`}</Typography>
      </Box>
      <Box sx={signupAccountExistsStyles.existingAccountForm()}>
        <TextField label="Password" type="password" required fullWidth autoComplete="current-password" value={inline.password} disabled={inline.isWorking} onChange={(event) => inline.onPasswordChange(event.target.value)} slotProps={{htmlInput: {'aria-label': 'Password'}}} sx={signupAccountExistsStyles.input()} />
        {inline.error && <Typography id="existing-account-error" role="alert" sx={signupAccountExistsStyles.actionError(theme)}>{inline.error}</Typography>}
        <Button type="submit" fullWidth variant="contained" disableElevation disabled={inline.isWorking} aria-describedby={inline.error ? 'existing-account-error' : undefined} sx={signupAccountExistsStyles.emailButton(theme)}>{inline.isWorking ? 'Signing in…' : 'Sign in'}</Button>
      </Box>
      <Button type="button" fullWidth variant="outlined" disabled={inline.isWorking} onClick={inline.onUseDifferentAccount} sx={signupAccountExistsStyles.outlinedAction(theme)}>Use a different account</Button>
    </Box>
  );
}
